import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { InventoryData } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getInventory } from '@/db/inventory'
import DatacenterExperience from '@/components/datacenter/experience'
import HallLoading from '@/components/datacenter/hall-loading'

interface HomeProps {
    inventory: InventoryData
    error: string | null
    origin: string
}

const firstHeader = (value: string | string[] | undefined): string | undefined => Array.isArray(value) ? value[0] : value

export const getServerSideProps: GetServerSideProps<HomeProps> = async ({ req }) => {
    const host = firstHeader(req.headers['x-forwarded-host']) ?? req.headers.host ?? ''
    const protocol = firstHeader(req.headers['x-forwarded-proto'])?.split(',')[0] ?? 'https'
    const origin = host ? `${protocol}://${host}` : ''

    try {
        const inventory = await getInventory()

        return { props: { inventory, error: null, origin } }
    }
    catch (error) {
        console.error('The data hall inventory could not be loaded:', error instanceof Error ? error.message : error)

        return {
            props: {
                inventory: { facility: null, racks: [], devices: [] },
                error: 'unavailable',
                origin
            }
        }
    }
}

// Props can be briefly missing while the editor hot-swaps this page before its
// fresh server data arrives, so render a loading state instead of the hall then.
const Home: FC<Partial<HomeProps>> = ({ inventory, error = null, origin = '' }): ReactNode => {
    return (
        <Layout>
            <Seo ogImage={origin ? `${origin}/images/og-datacenter.jpg` : '/images/og-datacenter.jpg'} />

            {inventory ? <DatacenterExperience inventory={inventory} error={error} /> : <HallLoading />}
        </Layout>
    )
}

export default Home
